import { FormsWidget, SEEN, type SeenFrame } from "./forms";

// Injected by build.mjs per environment (and by vitest.config.ts in tests).
declare const __FORMS_BASE__: string;

// The snippet marks its own iframe rather than configuring the script tag, so
// one bundle serves every embed on a page and nothing about how a form looks
// has to be repeated onto the tag that renders it. The attribute's value is the
// kind of asset, which is what keeps a second asset type from needing a second
// snippet contract.
//
// A frame that already has its widget carries SEEN, set by FormsWidget, so a
// page carrying two snippets, two copies of this bundle each scanning the whole
// document, does the work once. A marked frame without it is a page's rebuilt
// copy, which FormsWidget takes over.
const EMBEDS = "iframe[data-aivry-embed]";

/**
 * Renders every embed on the page. Never throws: an uncaught error lands in the
 * customer's own error tracking as a page error on their site, and aborts the
 * rest of the tag wherever an admin has pasted the snippet into a Google Tag
 * Manager custom HTML block. One embed failing must not cost them the next one.
 */
export function attachEmbeds(): void {
  for (const host of document.querySelectorAll<SeenFrame>(EMBEDS)) {
    if (host[SEEN]) continue;
    try {
      attach(host);
    } catch (error) {
      console.error("aivry.js: failed to render an embed", error);
    }
  }
}

/**
 * Renders the embeds on the page now and any added to it later: a snippet a
 * popup plugin or tag manager inserts after load, and a popup the page rebuilds
 * from saved HTML each time it opens. Mutation records arrive before the added
 * frame can fire load or post a message, so nothing it says is missed.
 */
export function watchEmbeds(): MutationObserver {
  attachEmbeds();
  const observer = new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some(holdsEmbed)))
      attachEmbeds();
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  return observer;
}

function holdsEmbed(node: Node): boolean {
  return (
    node instanceof Element &&
    (node.matches(EMBEDS) || node.querySelector(EMBEDS) !== null)
  );
}

function attach(host: HTMLIFrameElement): void {
  const type = host.dataset.aivryEmbed;
  if (type !== "form") {
    // An asset newer than the bundle cached on this visitor's machine. Its
    // iframe is left exactly as the snippet wrote it, so the visitor still gets
    // the asset, just without the parent page's params.
    console.warn(`aivry.js: ignoring unknown embed type "${type}"`);
    return;
  }

  const embedKey = host.dataset.aivryEmbedKey;
  if (!embedKey) {
    console.error(
      "aivry.js: data-aivry-embed-key is required; leaving the iframe as it is",
    );
    return;
  }

  const formUrl = `${__FORMS_BASE__}/forms/${encodeURIComponent(embedKey)}`;
  warnOnEnvironmentMismatch(host, formUrl);

  new FormsWidget(host, formUrl);
}

// The snippet's own src is the fallback form, pointing at whichever
// environment's CRM generated it; the frame this script renders comes from the
// base baked into this bundle instead. A snippet pasted onto a page loading a
// different environment's bundle therefore shows one environment's form beside
// a fallback for another, and nothing anywhere says so. Publishing guards this
// from the other end — build.mjs pins FORMS_BASE and deploy.sh --env rejects a
// bundle for the wrong bucket — but neither can see where a snippet was copied
// from.
//
// Guarded rather than trusted: a snippet whose src is missing or unparseable
// still has to get its form, which the baked base can render without it.
function warnOnEnvironmentMismatch(
  host: HTMLIFrameElement,
  formUrl: string,
): void {
  const snippetOrigin = originOf(host.src);
  if (!snippetOrigin || snippetOrigin === originOf(formUrl)) return;

  console.warn(
    `aivry.js: this snippet points at ${snippetOrigin}, but this bundle renders forms from ${__FORMS_BASE__}`,
  );
}

function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}
